import React, { useMemo, useState } from 'react';

import { useCourseData, CourseSeason } from '../hooks/useCourseData';
import { useContentCourse } from '../context/ContentContext';
import apiBackoffice from '../../../../services/apiBackoffice';
import { useToast } from '../../../../hooks/toast';
import { useAdminDelete } from '../../Admin/hooks/useAdminDelete';
import {
  ContentEditorLayout,
  Confirm,
  useContentEditor,
} from '../../../../components/Admin';
import Field from '../../../../components/Admin/Field';
import { TextInput, TextArea } from '../../../../components/Admin/Field/styles';

interface ModuleValues {
  title: string;
  description: string;
  position: string;
}

const EMPTY: ModuleValues = {
  title: '',
  description: '',
  position: '',
};

// Os IDs de módulo existentes são legíveis (ex: INTRODUCAO) e entram no ID das aulas.
// O ID sai do título; se o curso já tiver um igual, ganha um número no fim.
const buildSeasonId = (title: string, seasons: CourseSeason[]) => {
  const base =
    title
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 40) || 'MODULO';

  const used = new Set(seasons.map((season) => season.seasonid));
  let seasonId = base;
  for (let n = 2; used.has(seasonId); n++) seasonId = `${base}${n}`;

  return seasonId;
};

const ModuleForm: React.FC = () => {
  const { addToast } = useToast();
  const { courseId } = useContentCourse();
  const { seasons, loadingSeasons, selectedCourseId, handleCourseChange } =
    useCourseData(courseId);
  const { deleteItem, deletingId } = useAdminDelete();

  const { values, setField, selectedId, editing, dirty, select, clear, markSaved } =
    useContentEditor<ModuleValues>(EMPTY);

  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Os módulos existentes começam na posição 0.
  const nextPosition = useMemo(
    () =>
      seasons.reduce((next, season) => Math.max(next, (Number(season.position) || 0) + 1), 0),
    [seasons],
  );

  const pickerItems = useMemo(
    () =>
      seasons.map((season) => ({
        id: season.seasonid,
        label: season.title,
        hint: `posição ${season.position ?? 0}`,
      })),
    [seasons],
  );

  const onSelectItem = (seasonId: string) => {
    const season = seasons.find((item) => item.seasonid === seasonId);
    if (!season) return;

    select(seasonId, {
      title: season.title ?? '',
      description: season.description ?? '',
      position: String(season.position ?? 0),
    });
  };

  const validate = () => {
    if (!selectedCourseId) {
      addToast({
        type: 'error',
        title: 'Selecione o curso',
        description: 'Escolha o curso no topo da tela antes de salvar o módulo.',
      });
      return false;
    }
    if (!values.title.trim()) {
      addToast({ type: 'error', title: 'Título obrigatório' });
      return false;
    }

    const position = values.position.trim() === '' ? nextPosition : Number(values.position);
    if (!Number.isInteger(position) || position < 0) {
      addToast({
        type: 'error',
        title: 'Posição inválida',
        description: 'A posição deve ser um número inteiro, a partir de 0.',
      });
      return false;
    }
    return true;
  };

  const persist = async (seasonId: string) => {
    const position = values.position.trim() === '' ? nextPosition : Number(values.position);

    const body = {
      courseid: selectedCourseId,
      seasonid: seasonId,
      title: values.title.trim(),
      description: values.description.trim() || ' ',
      position,
    };

    setSaving(true);
    try {
      const response = await apiBackoffice.post('/backoffice/season/add', body);
      const ok = response.data === 'OK' || response.data?.message === 'OK';

      if (!ok) throw new Error('O servidor não confirmou a gravação.');

      addToast({ type: 'success', title: editing ? 'Módulo atualizado' : 'Módulo cadastrado' });
      markSaved();
      await handleCourseChange(selectedCourseId);
      return true;
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro ao salvar o módulo',
        description: err?.response?.data?.error || err?.message || 'Tente novamente.',
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const onSave = async () => {
    if (!validate()) return;

    if (editing) {
      await persist(selectedId);
      return;
    }

    const ok = await persist(buildSeasonId(values.title, seasons));
    if (ok) clear();
  };

  const onDelete = async () => {
    setConfirmDelete(false);
    if (!selectedId) return;

    const done = await deleteItem({
      entity: 'module',
      id: `${selectedCourseId}_${selectedId}`,
      successTitle: 'Módulo excluído',
    });

    if (done) {
      clear();
      await handleCourseChange(selectedCourseId);
    }
  };

  return (
    <>
      <ContentEditorLayout
        moduleOptions={[]}
        moduleId=""
        onModuleChange={() => undefined}
        pickerItems={pickerItems}
        pickerTitle="Módulos deste curso"
        selectedItemId={selectedId}
        onSelectItem={onSelectItem}
        pickerLoading={loadingSeasons}
        editing={editing}
        dirty={dirty}
        saving={saving}
        deleting={!!deletingId}
        onSave={onSave}
        onEdit={onSave}
        onDelete={() => setConfirmDelete(true)}
        onClear={clear}
      >
        <Field label="Título do módulo" required>
          <TextInput
            value={values.title}
            onChange={(event) => setField('title', event.target.value)}
            placeholder="Ex.: Introdução ao Aconselhamento Bíblico"
          />
        </Field>
        <Field
          label="Posição no curso"
          hint={`0 é o primeiro. Sugestão: ${nextPosition}`}
        >
          <TextInput
            value={values.position}
            onChange={(event) => setField('position', event.target.value)}
            placeholder={String(nextPosition)}
            inputMode="numeric"
          />
        </Field>

        <Field label="Descrição" full>
          <TextArea
            value={values.description}
            onChange={(event) => setField('description', event.target.value)}
            placeholder="Opcional"
          />
        </Field>
      </ContentEditorLayout>

      <Confirm
        open={confirmDelete}
        danger
        title="Excluir módulo"
        description="Só é possível se o módulo não tiver aulas. Não dá para desfazer."
        confirmLabel="Excluir"
        loading={!!deletingId}
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
};

export default ModuleForm;
